/**
 * BookTagsBar (iter-46, iter-48 threads) — free-text sticky notes any
 * workspace member can pin to a book. Each note supports a single level
 * of replies (comment threads) so teammates can discuss a task instead
 * of just tagging it. Author or admin can edit/delete their own notes;
 * deleting a root note cascades its replies on the server.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Pencil, Check, MessageSquarePlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function BookTagsBar({ bookId }) {
  const { user } = useAuth();
  const [tags, setTags] = useState([]);
  const [text, setText] = useState("");
  const [editing, setEditing] = useState(null); // {id, text}
  const [replyingTo, setReplyingTo] = useState(null); // {id, text}
  const [busy, setBusy] = useState(false);
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  const load = useCallback(async () => {
    if (!bookId) return;
    try {
      const { data } = await api.get(`/books/${bookId}/tags`);
      setTags(data || []);
    } catch (err) {
      console.debug("tags load failed:", err?.message);
    }
  }, [bookId]);
  useEffect(() => { load(); }, [load]);

  // Group flat rows into { root, replies[] } threads keyed by root id.
  const threads = useMemo(() => {
    const rootsById = {};
    const orderedRoots = [];
    for (const t of tags) {
      if (!t.parent_id) {
        rootsById[t.id] = { root: t, replies: [] };
        orderedRoots.push(t.id);
      }
    }
    for (const t of tags) {
      if (t.parent_id && rootsById[t.parent_id]) {
        rootsById[t.parent_id].replies.push(t);
      }
    }
    // Sort replies chronologically inside each thread.
    for (const id of orderedRoots) {
      rootsById[id].replies.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    }
    // Roots already come newest-first from the server; preserve that.
    return orderedRoots.map((id) => rootsById[id]);
  }, [tags]);

  const add = async (parentId = null) => {
    const raw = parentId ? (replyingTo?.text || "") : text;
    const t = (raw || "").trim();
    if (!t) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/books/${bookId}/tags`,
        parentId ? { text: t, parent_id: parentId } : { text: t });
      setTags((prev) => [data, ...prev]);
      if (parentId) setReplyingTo(null);
      else setText("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const commitEdit = async () => {
    if (!editing) return;
    const t = (editing.text || "").trim();
    if (!t) return setEditing(null);
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/books/${bookId}/tags/${editing.id}`, { text: t });
      setTags((prev) => prev.map((x) => x.id === editing.id ? data : x));
      setEditing(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (tag) => {
    const isRoot = !tag.parent_id;
    const msg = isRoot
      ? `Delete note "${tag.text}"? All replies will also be removed.`
      : `Delete reply "${tag.text}"?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/books/${bookId}/tags/${tag.id}`);
      setTags((prev) => prev.filter((x) => x.id !== tag.id
                                        && (isRoot ? x.parent_id !== tag.id : true)));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const renderRow = (t, opts = {}) => {
    const { isReply = false } = opts;
    const mine = t.user_id === user?.id;
    const canEdit = mine || isAdmin;
    const isEditing = editing?.id === t.id;
    return (
      <div key={t.id}
           className={"flex items-start gap-2 py-1.5 " + (isReply ? "pl-6" : "")}
           data-testid={`book-tag-${t.id}${isReply ? "-reply" : ""}`}>
        <span className={"inline-block w-1 self-stretch " + (mine ? "bg-[color:var(--primary)]" : "bg-[color:var(--border)]")}/>
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <input autoFocus value={editing.text}
                     onChange={(e)=>setEditing({ ...editing, text: e.target.value })}
                     onKeyDown={(e)=>{ if(e.key==="Enter") commitEdit(); if(e.key==="Escape") setEditing(null); }}
                     className="flex-1 text-xs border border-[color:var(--border)] px-2 py-1 focus:outline-none focus:border-[color:var(--primary)]"
                     data-testid={`book-tag-${t.id}-input`}/>
              <button onClick={commitEdit} disabled={busy}
                      className="text-[color:var(--primary)] hover:opacity-80"
                      aria-label="Save"
                      data-testid={`book-tag-${t.id}-save`}>
                <Check className="w-3.5 h-3.5"/>
              </button>
            </div>
          ) : (
            <>
              <div className="text-xs">
                <span className="text-[color:var(--text-muted)] mr-1">{t.nickname}:</span>
                <span>{t.text}</span>
              </div>
              <div className="flex items-center gap-3 mt-0.5 text-[10px] text-[color:var(--text-muted)]">
                {!isReply && (
                  <button onClick={()=>setReplyingTo({ id: t.id, text: "" })}
                          className="inline-flex items-center gap-1 hover:text-[color:var(--primary)]"
                          data-testid={`book-tag-${t.id}-reply-btn`}>
                    <MessageSquarePlus className="w-3 h-3"/> Reply
                  </button>
                )}
                {canEdit && (
                  <>
                    <button onClick={()=>setEditing({ id: t.id, text: t.text })}
                            className="inline-flex items-center gap-1 hover:text-[color:var(--primary)]"
                            data-testid={`book-tag-${t.id}-edit`}>
                      <Pencil className="w-3 h-3"/> Edit
                    </button>
                    <button onClick={()=>remove(t)}
                            className="inline-flex items-center gap-1 hover:text-red-700"
                            data-testid={`book-tag-${t.id}-delete`}>
                      <X className="w-3 h-3"/> Delete
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="mt-4 border border-[color:var(--border)] bg-white px-3 py-2" data-testid="book-tags-bar">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)]">
          § Notes &amp; comments
        </span>
        <span className="text-[10px] text-[color:var(--text-muted)]">
          {threads.length} thread{threads.length === 1 ? "" : "s"}
        </span>
      </div>

      {threads.length === 0 && (
        <p className="text-[11px] italic text-[color:var(--text-muted)]" data-testid="book-tags-empty">
          No notes yet. Add one below — visible to everyone in this workspace.
        </p>
      )}

      <div className="divide-y divide-[color:var(--border)]">
        {threads.map(({ root, replies }) => (
          <div key={root.id} className="py-1" data-testid={`book-tag-thread-${root.id}`}>
            {renderRow(root)}
            {replies.map((r) => renderRow(r, { isReply: true }))}
            {replyingTo?.id === root.id && (
              <div className="pl-6 pt-1 flex items-center gap-2"
                   data-testid={`book-tag-reply-form-${root.id}`}>
                <input autoFocus value={replyingTo.text}
                       onChange={(e)=>setReplyingTo({ ...replyingTo, text: e.target.value })}
                       onKeyDown={(e)=>{ if(e.key==="Enter") add(root.id); if(e.key==="Escape") setReplyingTo(null); }}
                       placeholder="Write a reply…"
                       maxLength={140}
                       className="flex-1 text-xs border border-[color:var(--border)] px-2 py-1 focus:outline-none focus:border-[color:var(--primary)]"
                       data-testid={`book-tag-reply-input-${root.id}`}/>
                <button onClick={()=>add(root.id)} disabled={busy || !replyingTo.text.trim()}
                        className="btn-ghost text-xs"
                        data-testid={`book-tag-reply-submit-${root.id}`}>
                  Reply
                </button>
                <button onClick={()=>setReplyingTo(null)}
                        className="text-[color:var(--text-muted)] hover:text-red-700"
                        aria-label="Cancel"
                        data-testid={`book-tag-reply-cancel-${root.id}`}>
                  <X className="w-3.5 h-3.5"/>
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-2 pt-2 border-t border-[color:var(--border)] flex items-center gap-2">
        <input value={text}
               onChange={(e)=>setText(e.target.value)}
               onKeyDown={(e)=>{ if(e.key==="Enter") add(); }}
               placeholder='Start a new thread — e.g. "All TOCs uploaded"'
               maxLength={140}
               className="flex-1 border border-[color:var(--border)] px-2 py-1 text-xs focus:outline-none focus:border-[color:var(--primary)]"
               data-testid="book-tag-input"/>
        <button onClick={()=>add()} disabled={busy || !text.trim()}
                className="btn-ghost text-xs inline-flex items-center gap-1"
                data-testid="book-tag-add-btn">
          <Plus className="w-3 h-3"/> Add note
        </button>
      </div>
    </div>
  );
}
